import { Container } from '@/components/layout/Container';
import { Stack } from '@/components/layout/Stack';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Link } from 'react-router-dom';
import { FadeIn, StaggerContainer, StaggerItem } from '@/components/ui/AnimatedCard';
import { Shield, Database, Lock, Globe, Mail } from 'lucide-react';

export function PrivacyPolicy() {
  const lastUpdated = 'December 7, 2025';
  const effectiveDate = 'December 7, 2025';

  return (
    <Container size="lg" className="py-6">
      <Stack spacing="lg">
        {/* Header */}
        <FadeIn>
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="rounded-full bg-blue-100 p-3">
                <Shield className="h-8 w-8 text-blue-600" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Privacy Policy
            </h1>
            <p className="text-muted-foreground">
              Last updated: {lastUpdated}
            </p>
          </div>
        </FadeIn>

        {/* Quick Overview */}
        <Card>
          <CardHeader>
            <CardTitle>Your Privacy at a Glance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-start gap-3">
                <Lock className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <h4 className="font-medium">Your Data is Secure</h4>
                  <p className="text-sm text-muted-foreground">
                    We use encryption and secure protocols to protect your information.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Database className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <h4 className="font-medium">Your Data Stays Local</h4>
                  <p className="text-sm text-muted-foreground">
                    Your content is stored on your device or through your own storage choices.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Globe className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <h4 className="font-medium">We Don't Sell Data</h4>
                  <p className="text-sm text-muted-foreground">
                    Your personal information is never sold to third parties.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table of Contents */}
        <Card>
          <CardHeader>
            <CardTitle>Table of Contents</CardTitle>
          </CardHeader>
          <CardContent>
            <nav className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {[
                { id: 'introduction', label: '1. Introduction' },
                { id: 'information-we-collect', label: '2. Information We Collect' },
                { id: 'how-we-use', label: '3. How We Use Your Information' },
                { id: 'data-sharing', label: '4. Data Sharing & Disclosure' },
                { id: 'data-security', label: '5. Data Security' },
                { id: 'your-rights', label: '6. Your Rights & Choices' },
                { id: 'cookies', label: '7. Cookies & Tracking' },
                { id: 'children', label: "8. Children's Privacy" },
                { id: 'international', label: '9. International Data Transfers' },
                { id: 'changes', label: '10. Changes to This Policy' },
                { id: 'contact', label: '11. Contact Us' },
              ].map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
                >
                  {item.label}
                </a>
              ))}
            </nav>
          </CardContent>
        </Card>

        {/* Policy Sections */}
        <StaggerContainer>
          <Stack spacing="md">
            {/* 1. Introduction */}
            <StaggerItem>
              <Card id="introduction">
                <CardHeader>
                  <CardTitle>1. Introduction</CardTitle>
                </CardHeader>
                <CardContent className="prose prose-sm max-w-none text-muted-foreground">
                  <p>
                    Welcome to GodMode ("we," "our," or "us"). We are committed to protecting
                    your privacy and ensuring the security of your personal information.
                  </p>
                  <p>
                    This Privacy Policy explains how we collect, use, disclose, and safeguard
                    your information when you use our productivity application and related services
                    (collectively, the "Service"). By using GodMode, you agree to the collection
                    and use of information in accordance with this policy.
                  </p>
                  <p>
                    <strong>Effective Date:</strong> {effectiveDate}
                  </p>
                </CardContent>
              </Card>
            </StaggerItem>

            {/* 2. Information We Collect */}
            <StaggerItem>
              <Card id="information-we-collect">
                <CardHeader>
                  <CardTitle>2. Information We Collect</CardTitle>
                </CardHeader>
                <CardContent className="prose prose-sm max-w-none text-muted-foreground">
                  <h4 className="text-foreground font-medium">2.1 Information You Provide</h4>
                  <ul>
                    <li><strong>Account Information:</strong> Email address, name, and password when you create an account</li>
                    <li><strong>Profile Information:</strong> Optional profile picture and display preferences</li>
                    <li><strong>Payment Information:</strong> Billing details processed securely through Stripe (we do not store full payment card numbers)</li>
                  </ul>

                  <h4 className="text-foreground font-medium">2.2 Your Content Data</h4>
                  <p>
                    Aspirations, goals, lists, tasks, and notes you create in the app are stored locally on your
                    device or through your own storage choices. <strong>We do not collect or have access to this
                    content data.</strong> You maintain full control over where your productivity data is stored.
                  </p>

                  <h4 className="text-foreground font-medium">2.3 Information Collected Automatically</h4>
                  <ul>
                    <li><strong>Device Information:</strong> Browser type, operating system, device type</li>
                    <li><strong>Usage Data:</strong> Features used, pages visited, time spent in app</li>
                    <li><strong>Log Data:</strong> IP address, access times, error logs</li>
                    <li><strong>Cookies:</strong> Session and preference cookies (see Section 7)</li>
                  </ul>

                  <h4 className="text-foreground font-medium">2.4 Third-Party Authentication</h4>
                  <p>
                    If you sign in with Google, we receive your email address and profile information
                    as permitted by your Google account settings.
                  </p>
                </CardContent>
              </Card>
            </StaggerItem>

            {/* 3. How We Use Your Information */}
            <StaggerItem>
              <Card id="how-we-use">
                <CardHeader>
                  <CardTitle>3. How We Use Your Information</CardTitle>
                </CardHeader>
                <CardContent className="prose prose-sm max-w-none text-muted-foreground">
                  <p>We use your information to:</p>
                  <ul>
                    <li>Provide, maintain, and improve the Service</li>
                    <li>Send service-related notifications and updates</li>
                    <li>Process payments and manage subscriptions</li>
                    <li>Provide customer support</li>
                    <li>Analyze usage patterns to improve features</li>
                    <li>Detect and prevent fraud or abuse</li>
                    <li>Comply with legal obligations</li>
                  </ul>
                  <p>
                    <strong>Note:</strong> Your content data (aspirations, goals, lists, tasks, notes) remains
                    stored locally on your device or through your own storage configuration. We do not access
                    or process this content.
                  </p>
                  <p>
                    <strong>AI Features:</strong> If you use AI-powered features (such as smart
                    scheduling or form assistance), your relevant data may be processed by our
                    AI service providers to provide these features. This data is not used to
                    train AI models.
                  </p>
                </CardContent>
              </Card>
            </StaggerItem>

            {/* 4. Data Sharing & Disclosure */}
            <StaggerItem>
              <Card id="data-sharing">
                <CardHeader>
                  <CardTitle>4. Data Sharing & Disclosure</CardTitle>
                </CardHeader>
                <CardContent className="prose prose-sm max-w-none text-muted-foreground">
                  <p><strong>We do not sell your personal information.</strong></p>
                  <p>We may share information with:</p>
                  <ul>
                    <li><strong>Service Providers:</strong> Third parties that help us operate the Service
                      (hosting, payment processing, analytics)</li>
                    <li><strong>Legal Requirements:</strong> When required by law, court order, or
                      government request</li>
                    <li><strong>Business Transfers:</strong> In connection with a merger, acquisition,
                      or sale of assets</li>
                    <li><strong>With Your Consent:</strong> When you explicitly agree to share information</li>
                  </ul>

                  <h4 className="text-foreground font-medium">Our Service Providers</h4>
                  <ul>
                    <li><strong>Supabase:</strong> Database and authentication</li>
                    <li><strong>Stripe:</strong> Payment processing</li>
                    <li><strong>Vercel:</strong> Hosting and analytics</li>
                    <li><strong>OpenAI/Anthropic:</strong> AI feature processing</li>
                  </ul>
                </CardContent>
              </Card>
            </StaggerItem>

            {/* 5. Data Security */}
            <StaggerItem>
              <Card id="data-security">
                <CardHeader>
                  <CardTitle>5. Data Security</CardTitle>
                </CardHeader>
                <CardContent className="prose prose-sm max-w-none text-muted-foreground">
                  <p>We implement industry-standard security measures:</p>
                  <ul>
                    <li>HTTPS/TLS encryption for all data in transit</li>
                    <li>Encryption at rest for stored data</li>
                    <li>Secure password hashing</li>
                    <li>Regular security audits</li>
                    <li>Access controls and monitoring</li>
                  </ul>
                  <p>
                    While we strive to protect your information, no method of transmission or
                    storage is 100% secure. We encourage you to use strong passwords and enable
                    two-factor authentication when available.
                  </p>
                </CardContent>
              </Card>
            </StaggerItem>

            {/* 6. Your Rights & Choices */}
            <StaggerItem>
              <Card id="your-rights">
                <CardHeader>
                  <CardTitle>6. Your Rights & Choices</CardTitle>
                </CardHeader>
                <CardContent className="prose prose-sm max-w-none text-muted-foreground">
                  <p>You have the right to:</p>
                  <ul>
                    <li><strong>Access:</strong> Request a copy of your data</li>
                    <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                    <li><strong>Deletion:</strong> Request deletion of your account and data</li>
                    <li><strong>Export:</strong> Download your data in a portable format</li>
                    <li><strong>Opt-out:</strong> Unsubscribe from marketing emails</li>
                  </ul>

                  <h4 className="text-foreground font-medium">GDPR Rights (EU Users)</h4>
                  <p>EU residents have additional rights including data portability, restriction
                  of processing, and the right to lodge a complaint with a supervisory authority.</p>

                  <h4 className="text-foreground font-medium">CCPA Rights (California Users)</h4>
                  <p>California residents have the right to know what personal information is
                  collected, request deletion, and opt-out of sale of personal information
                  (note: we do not sell personal information).</p>

                  <p>
                    To exercise these rights, visit your <Link to="/settings" className="text-primary hover:underline">Settings</Link> page
                    or contact us at the address below.
                  </p>
                </CardContent>
              </Card>
            </StaggerItem>

            {/* 7. Cookies & Tracking */}
            <StaggerItem>
              <Card id="cookies">
                <CardHeader>
                  <CardTitle>7. Cookies & Tracking</CardTitle>
                </CardHeader>
                <CardContent className="prose prose-sm max-w-none text-muted-foreground">
                  <p>We use cookies and similar technologies for:</p>
                  <ul>
                    <li><strong>Essential Cookies:</strong> Required for authentication and security</li>
                    <li><strong>Preference Cookies:</strong> Remember your settings (theme, language)</li>
                    <li><strong>Analytics Cookies:</strong> Understand how the Service is used</li>
                  </ul>
                  <p>
                    You can manage cookie preferences in your browser settings. Disabling essential
                    cookies may affect Service functionality.
                  </p>
                </CardContent>
              </Card>
            </StaggerItem>

            {/* 8. Children's Privacy */}
            <StaggerItem>
              <Card id="children">
                <CardHeader>
                  <CardTitle>8. Children's Privacy</CardTitle>
                </CardHeader>
                <CardContent className="prose prose-sm max-w-none text-muted-foreground">
                  <p>
                    GodMode is not intended for children under 13. We do not knowingly collect
                    personal information from children under 13. If we learn that we have collected
                    such information, we will delete it promptly.
                  </p>
                  <p>
                    Users between 13 and 18 may use the Service with parental consent.
                  </p>
                </CardContent>
              </Card>
            </StaggerItem>

            {/* 9. International Data Transfers */}
            <StaggerItem>
              <Card id="international">
                <CardHeader>
                  <CardTitle>9. International Data Transfers</CardTitle>
                </CardHeader>
                <CardContent className="prose prose-sm max-w-none text-muted-foreground">
                  <p>
                    Your information may be transferred to and processed in countries other than
                    your own. We ensure appropriate safeguards are in place for international
                    transfers, including Standard Contractual Clauses for EU data.
                  </p>
                </CardContent>
              </Card>
            </StaggerItem>

            {/* 10. Changes to This Policy */}
            <StaggerItem>
              <Card id="changes">
                <CardHeader>
                  <CardTitle>10. Changes to This Policy</CardTitle>
                </CardHeader>
                <CardContent className="prose prose-sm max-w-none text-muted-foreground">
                  <p>
                    We may update this Privacy Policy from time to time. We will notify you of
                    significant changes by email or through the Service. Your continued use of
                    GodMode after changes constitutes acceptance of the updated policy.
                  </p>
                </CardContent>
              </Card>
            </StaggerItem>

            {/* 11. Contact Us */}
            <StaggerItem>
              <Card id="contact">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="h-5 w-5" />
                    11. Contact Us
                  </CardTitle>
                </CardHeader>
                <CardContent className="prose prose-sm max-w-none text-muted-foreground">
                  <p>
                    If you have questions about this Privacy Policy or wish to exercise your
                    data rights, please contact us:
                  </p>
                  <ul>
                    <li><strong>Support:</strong> <Link to="/help" className="text-primary hover:underline">Help Center</Link></li>
                  </ul>
                  <p>
                    We aim to respond to all requests within 30 days.
                  </p>
                </CardContent>
              </Card>
            </StaggerItem>
          </Stack>
        </StaggerContainer>

        {/* Related Links */}
        <Card>
          <CardHeader>
            <CardTitle>Related Documents</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/terms"
                className="text-sm text-primary hover:underline"
              >
                Terms of Service
              </Link>
              <Link
                to="/help"
                className="text-sm text-primary hover:underline"
              >
                Help Center
              </Link>
              <Link
                to="/settings"
                className="text-sm text-primary hover:underline"
              >
                Manage Your Data
              </Link>
            </div>
          </CardContent>
        </Card>
      </Stack>
    </Container>
  );
}
